import getCharacters, {
  type Character,
} from '@cartwhl/common/api/getCharacters';
import getExternalCharacter from '@cartwhl/common/api/getExternalCharacter';
import getExternalCharacters, {
  fetchHeadBoneNameFromConfigUrl,
  mapExternalCharacterListItemToCharacter,
} from '@cartwhl/common/api/getExternalCharacters';
import getScene from '@cartwhl/common/api/getScene';
import postExternalSceneReference from '@cartwhl/common/api/postExternalSceneReference';
import putExternalScene from '@cartwhl/common/api/putExternalScene';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import type {
  AsyncState,
  Scene,
  SceneTimelineUpdateItem,
} from '@cartwhl/common/types';
import {
  FACE_EXPRESSIONS,
  HAND_OPTIONS,
  SceneType,
} from '@cartwhl/common/types';
import { waitForAsyncState } from '@cartwhl/common/utils';

import * as globalStores from '../../stores/global';
import type { AppletWebSocketEventMap } from '../../stores/webSocketStore';
import { resolveCharacterMeshFormat } from '../shared/characterMeshFormat';
import {
  getBuiltinFreeCharacterMeshPath,
  stripCharacterIdInstanceSuffix,
} from '../shared/freeCharacterMesh';
import {
  DEFAULT_CHARACTER,
  isLegacyMotionEditorCharacterId,
  resolveIsLegacyMotionEditorCharacter,
} from './constants';
import {
  type MotionEditorSceneCreateProgress,
  createMotionEditorSceneFromExistingMotion,
} from './store/sceneCreateUtils';
import {
  createMotionEditorSceneFromScene,
  deriveMotionEditorScenePlayback,
  getDefaultSelectedMotion,
  getFirstMotionEditorActor,
  getMotionEditorActorByReferenceName,
  getSelectedMotionFromValue,
} from './store/scenePlaybackUtils';
import type {
  CharacterData,
  MotionEditorActor,
  MotionEditorCharacterSettings,
  MotionEditorScene,
  SceneReferenceCharacterState,
  SelectedSceneMotion,
} from './types';

export type MotionEditorStoreDeps = { global: typeof globalStores };

type RefreshSceneAfterTimelineMutationInput = {
  apiKey: string;
  referenceName: string;
  sceneID: string;
  timelineIndex: number;
};

type PreferredTimelineSelection = {
  referenceName: string;
  timelineIndex: number;
};

type CharactersState = {
  /** Character picker catalog. Loaded only when the picker needs it. */
  availableCharacters: AsyncState<Character[]>;
  /** Targeted character metadata needed by the currently loaded scene. */
  sceneCharactersByID: Record<string, Character>;
  resolvingMotionCharacter: boolean;
  /** Character picker state keyed by scene reference name. */
  byReferenceName: Record<string, SceneReferenceCharacterState>;
  /** Active reference (mirrors the selected entry in `byReferenceName`). */
  selectedCharacter: CharacterData | null;
  characterSettings: MotionEditorCharacterSettings;
};

type PlaybackState = {
  sceneActors: MotionEditorActor[];
  selectedReferenceName: string | null;
  selectedSceneMotion: SelectedSceneMotion | null;
};

export type ActorEditingState = {
  selectedMotionValue: string | null;
};

export type EditingState = {
  actorEditStatesById: Record<string, ActorEditingState>;
};

export type MotionEditorState = {
  scene: AsyncState<MotionEditorScene>;
  characters: CharactersState;
  playback: PlaybackState;
  editing: EditingState;
  /** Optional text prompt describing the desired motion for key pose regeneration. */
  motionDescription: string;
};

const DEFAULT_CHARACTER_SETTINGS: MotionEditorCharacterSettings = {
  charUploadID: DEFAULT_CHARACTER.jobID,
  isLegacyCharacter: false,
  skinHex: 'default',
  faceExpression: FACE_EXPRESSIONS.DEFAULT,
  staticHands: HAND_OPTIONS.DEFAULT,
};

const initialState: MotionEditorState = {
  scene: { status: 'initial', data: undefined },
  characters: {
    availableCharacters: { status: 'initial', data: undefined },
    sceneCharactersByID: {},
    byReferenceName: {},
    selectedCharacter: {
      characterID: DEFAULT_CHARACTER.jobID,
      meshPath: DEFAULT_CHARACTER.meshPath,
      rotationOffsets: DEFAULT_CHARACTER.rotationOffsets,
    },
    resolvingMotionCharacter: false,
    characterSettings: DEFAULT_CHARACTER_SETTINGS,
  },
  playback: {
    sceneActors: [],
    selectedReferenceName: null,
    selectedSceneMotion: null,
  },
  editing: createInitialEditingState(),
  motionDescription: '',
};

export class MotionEditorStore extends BaseStore<
  MotionEditorState,
  MotionEditorStoreDeps,
  AppletWebSocketEventMap
> {
  private sceneApplyRequestID = 0;
  private readonly sceneCharacterLoadIDs = new Set<string>();
  private readonly sceneCharacterRequestsByID = new Map<string, Promise<void>>();
  private activeSceneLoad: { key: string; promise: Promise<void> } | null = null;

  readonly sceneLoader = this.createLoader('scene', async (sceneID: string) => {
    const apiKeyData = await this.getApiKeyData();
    if (!apiKeyData) {
      return {
        error: { message: 'Failed to authenticate. Please try again.' },
      };
    }

    const { data, error } = await getScene(sceneID, apiKeyData.apiKey);
    if (error) {
      return { error };
    }
    if (!data) {
      return { error: { message: 'Failed to load scene.' } };
    }
    if (data.type !== SceneType.MOTION) {
      return {
        error: { message: 'Selected scene is not a motion scene.' },
      };
    }

    await this.ensureSceneCharactersForApiScene(data);
    await this.resolveHeadBoneNamesForSceneObjects(data);
    const motionScene = this.createMotionEditorScene(data);
    this.startSceneApply();

    return { data: motionScene };
  });

  constructor(deps: MotionEditorStoreDeps) {
    super(initialState, deps);
  }

  init() {
    this.websocket.on('TIMELINE_UPDATED', (event) => {
      const sceneID = event.sceneID ?? event.projectID;
      if (sceneID) {
        void this.refreshSceneFromTimelineUpdate(sceneID);
      }
    });
  }

  reset() {
    this.sceneLoader.reset();
    this.state.characters.availableCharacters = {
      status: 'initial',
      data: undefined,
    };
    this.state.characters.sceneCharactersByID = {};
    this.sceneCharacterLoadIDs.clear();
    this.sceneCharacterRequestsByID.clear();
    this.sceneApplyRequestID += 1;
    this.activeSceneLoad = null;
    this.state.playback.sceneActors = [];
    this.state.playback.selectedReferenceName = null;
    this.state.playback.selectedSceneMotion = null;
    this.state.characters.byReferenceName = {};
    this.setDefaultSelectedCharacterContext();
    this.state.editing = createInitialEditingState();
    this.state.motionDescription = '';
  }

  setMotionDescription(motionDescription: string) {
    this.state.motionDescription = motionDescription;
  }

  setCharacterSettings(
    nextSettings: Partial<MotionEditorCharacterSettings>,
    referenceName = this.state.playback.selectedReferenceName,
  ) {
    if (!referenceName) {
      this.state.characters.characterSettings = {
        ...this.state.characters.characterSettings,
        ...nextSettings,
      };
      return;
    }

    const currentReferenceState =
      this.getReferenceCharacterState(referenceName);
    const nextCharacterSettings = {
      ...currentReferenceState.characterSettings,
      ...nextSettings,
    };

    this.state.characters.byReferenceName[referenceName] = {
      ...currentReferenceState,
      characterSettings: nextCharacterSettings,
    };

    if (this.state.playback.selectedReferenceName === referenceName) {
      this.state.characters.characterSettings = nextCharacterSettings;
    }
  }

  getReferenceCharacterState(
    referenceName: string,
  ): SceneReferenceCharacterState {
    const cachedState = this.state.characters.byReferenceName[referenceName];
    if (cachedState) {
      return cachedState;
    }

    if (this.state.scene.data) {
      return this.hydrateReferenceCharacterStateFromScene(referenceName);
    }

    return {
      selectedCharacter: null,
      characterSettings: DEFAULT_CHARACTER_SETTINGS,
    };
  }

  getCharacterSettingsForReference(referenceName: string) {
    return this.getReferenceCharacterState(referenceName).characterSettings;
  }

  getSelectedSceneReference() {
    return getMotionEditorActorByReferenceName(
      this.state.playback.sceneActors,
      this.state.playback.selectedReferenceName,
    );
  }

  async loadAvailableCharacters(limit = 100) {
    this.state.characters.availableCharacters = {
      status: 'pending',
      data: this.state.characters.availableCharacters.data,
    };

    const apiKeyData = await this.getApiKeyData();

    if (apiKeyData?.apiKey) {
      const { data, error } = await getExternalCharacters(apiKeyData.apiKey, {
        limit,
        sortAscending: false,
      });
      const rawItems = data?.characters ?? [];
      const characters: Character[] = rawItems.map((item) =>
        mapExternalCharacterListItemToCharacter(item),
      );

      if (characters.length === 0) {
        this.state.characters.availableCharacters = {
          status: 'error',
          data: undefined,
          error: error?.message ?? 'Failed to load characters.',
        };
        this.deps.global.noticesStore.error(
          error?.message ?? 'Failed to load characters.',
        );
        return;
      }

      this.state.characters.availableCharacters = {
        status: 'ready',
        data: characters,
      };
      this.syncSelectedCharacter(characters);
      return;
    }

    const { data, error } = await getCharacters(limit);
    const characters = data?.characters ?? [];

    if (characters.length === 0) {
      this.state.characters.availableCharacters = {
        status: 'error',
        data: undefined,
        error: error?.message ?? 'Failed to load characters.',
      };
      this.deps.global.noticesStore.error(
        error?.message ?? 'Failed to load characters.',
      );
      return;
    }

    this.state.characters.availableCharacters = {
      status: 'ready',
      data: characters,
    };
    this.syncSelectedCharacter(characters);
  }

  async selectCharacter(
    characterID: string,
    referenceName = this.getActiveReferenceNameForCharacterSwap(),
  ) {
    const scene = this.state.scene.data;

    if (scene) {
      if (!referenceName) {
        this.deps.global.noticesStore.error(
          'Select a scene reference before changing its character.',
        );
        return;
      }

      await this.updateSceneReferenceCharacter(
        scene,
        referenceName,
        characterID,
      );
      return;
    }

    this.state.characters.resolvingMotionCharacter = false;
    this.state.characters.selectedCharacter =
      this.getCharacterDataById(characterID) ??
      this.state.characters.selectedCharacter;
    this.state.characters.characterSettings = {
      ...this.state.characters.characterSettings,
      charUploadID: characterID,
      skinHex: 'default',
      isLegacyCharacter: this.resolveIsLegacyCharacter(characterID),
    };
  }

  async changeAllSceneCharactersToDefault() {
    const scene = this.state.scene.data;
    if (!scene) {
      this.deps.global.noticesStore.error('Scene not loaded.');
      return false;
    }

    const sceneObjectEntries = Object.entries(scene.objects);
    if (sceneObjectEntries.length === 0) {
      this.deps.global.noticesStore.error('Scene reference not found.');
      return false;
    }

    const apiKeyData = await this.getApiKeyData();
    if (!apiKeyData) {
      this.deps.global.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      return false;
    }

    const previousSelectedMotionValue =
      this.state.playback.selectedSceneMotion?.motionValue;
    const previousSelectedReferenceName =
      this.state.playback.selectedReferenceName;

    this.state.characters.resolvingMotionCharacter = true;
    this.state.editing.actorEditStatesById = {};

    let latestScene: Scene | null = null;
    for (const [referenceName, sceneObject] of sceneObjectEntries) {
      const { data, error } = await postExternalSceneReference(
        apiKeyData.apiKey,
        scene.sceneID,
        referenceName,
        {
          objectID: DEFAULT_CHARACTER.jobID,
          stitchFromIndex: 0,
          timeline: sceneObject.timeline.map(sceneTimelineItemToUpdateItem),
        },
      );

      if (error || !data) {
        if (latestScene) {
          this.applySceneFromApi(latestScene, {
            selectedMotionValue: previousSelectedMotionValue,
            preferredReferenceName: previousSelectedReferenceName,
          });
        } else {
          this.state.characters.resolvingMotionCharacter = false;
        }

        this.deps.global.noticesStore.error(
          error?.message ?? 'Failed to update scene character.',
        );
        return false;
      }

      latestScene = data;
    }

    if (!latestScene) {
      this.state.characters.resolvingMotionCharacter = false;
      return true;
    }

    this.applySceneFromApi(latestScene, {
      selectedMotionValue: previousSelectedMotionValue,
      preferredReferenceName: previousSelectedReferenceName,
    });
    return true;
  }

  selectReference(referenceName: string) {
    if (this.state.playback.selectedReferenceName === referenceName) {
      return;
    }

    const reference = getMotionEditorActorByReferenceName(
      this.state.playback.sceneActors,
      referenceName,
    );
    if (!reference) {
      return;
    }

    this.hydrateReferenceCharacterStateFromScene(referenceName);
    this.applySelectedSceneReference(reference);
  }

  deselectReference() {
    if (this.state.playback.sceneActors.length <= 1) {
      return;
    }

    this.state.playback.selectedReferenceName = null;
    this.state.playback.selectedSceneMotion = null;
    this.clearSelectedCharacterContext();
    this.state.characters.resolvingMotionCharacter = false;
  }

  async refreshSceneAfterTimelineMutation({
    apiKey,
    referenceName,
    sceneID,
    timelineIndex,
  }: RefreshSceneAfterTimelineMutationInput) {
    const { data, error } = await getScene(sceneID, apiKey);
    if (error || !data || data.type !== SceneType.MOTION) {
      return false;
    }

    this.applySceneFromApi(data, {
      preferredReferenceName: referenceName,
      preferredTimelineSelection: {
        referenceName,
        timelineIndex,
      },
    });

    return true;
  }

  async loadScene(sceneID: string, selectedMotionValue?: string | null) {
    const loadKey = getSceneLoadKey(sceneID, selectedMotionValue);
    if (this.activeSceneLoad?.key === loadKey) {
      return this.activeSceneLoad.promise;
    }

    const promise = this.loadSceneFromApi(sceneID, selectedMotionValue);
    this.activeSceneLoad = { key: loadKey, promise };

    try {
      await promise;
    } finally {
      if (this.activeSceneLoad?.promise === promise) {
        this.activeSceneLoad = null;
      }
    }
  }

  async createSceneFromMotion(
    motionID: string,
    existingVariationNumber = 0,
    onProgress?: (progress: MotionEditorSceneCreateProgress) => void,
    outputCount = 1,
  ) {
    this.state.editing = createInitialEditingState();
    this.state.motionDescription = '';

    this.state.scene = { status: 'pending', data: undefined };
    this.state.playback.sceneActors = [];
    this.state.playback.selectedReferenceName = null;
    this.state.playback.selectedSceneMotion = null;
    this.state.characters.byReferenceName = {};
    this.clearSelectedCharacterContext();

    try {
      const apiKeyData = await this.getApiKeyData();
      if (!apiKeyData) {
        throw new Error('Failed to authenticate. Please try again.');
      }

      const scene = await createMotionEditorSceneFromExistingMotion({
        apiKey: apiKeyData.apiKey,
        motionID,
        existingVariationNumber,
        outputCount,
        onProgress,
      });

      this.state.scene = { status: 'ready', data: scene };
      this.syncLoadedSceneContext(scene);
      return scene;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create scene.';
      this.state.scene = {
        status: 'error',
        data: undefined,
        error: message,
      };
      this.state.characters.resolvingMotionCharacter = false;
      this.deps.global.noticesStore.error(message);
      return null;
    }
  }

  async updateSceneName(sceneID: string, sceneName: string) {
    const nextSceneName = sceneName.trim();
    const currentScene = this.state.scene.data;
    if (!nextSceneName) {
      return false;
    }
    if (!currentScene || currentScene.sceneID !== sceneID) {
      this.deps.global.noticesStore.error('Scene not loaded.');
      return false;
    }
    if (currentScene.sceneName === nextSceneName) {
      return true;
    }

    const previousSelectedMotionValue =
      this.state.playback.selectedSceneMotion?.motionValue;
    const previousSelectedReferenceName =
      this.state.playback.selectedReferenceName;
    const apiKeyData = await this.getApiKeyData();
    if (!apiKeyData) {
      this.deps.global.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      return false;
    }

    const { data, error } = await putExternalScene(apiKeyData.apiKey, sceneID, {
      sceneName: nextSceneName,
    });

    if (error || !data) {
      this.deps.global.noticesStore.error(
        error?.message ?? 'Failed to rename scene.',
      );
      return false;
    }

    this.applySceneFromApi(data, {
      selectedMotionValue: previousSelectedMotionValue,
      preferredReferenceName: previousSelectedReferenceName,
    });
    return true;
  }

  protected onWebSocketConnectionError(error: unknown) {
    this.deps.global.noticesStore.error(
      error instanceof Error
        ? error.message
        : 'Failed to connect to scene update service.',
    );
  }

  private async loadSceneFromApi(
    sceneID: string,
    selectedMotionValue?: string | null,
  ) {
    this.state.editing = createInitialEditingState();
    this.state.motionDescription = '';

    this.state.playback.sceneActors = [];
    this.state.playback.selectedReferenceName = null;
    this.state.playback.selectedSceneMotion = null;
    this.state.characters.byReferenceName = {};
    this.clearSelectedCharacterContext();

    await this.sceneLoader.fetch(sceneID);

    if (this.state.scene.status !== 'ready' || !this.state.scene.data) {
      this.state.characters.resolvingMotionCharacter = false;
      return;
    }

    this.syncLoadedSceneContext(this.state.scene.data, selectedMotionValue);
  }

  private async refreshSceneFromTimelineUpdate(sceneID: string) {
    const previousSceneID = this.state.scene.data?.sceneID;
    if (sceneID !== previousSceneID) {
      return;
    }

    const previousSelectedMotionValue =
      this.state.playback.selectedSceneMotion?.motionValue;
    const previousSelectedReferenceName =
      this.state.playback.selectedReferenceName;
    const apiKeyData = await this.getApiKeyData();
    if (!apiKeyData) {
      this.deps.global.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      return;
    }

    const { data, error } = await getScene(sceneID, apiKeyData.apiKey);

    if (error || !data) {
      this.deps.global.noticesStore.error(
        error?.message ?? 'Failed to refresh scene.',
      );
      return;
    }

    if (data.type !== SceneType.MOTION) {
      this.deps.global.noticesStore.error(
        'Selected scene is not a motion scene.',
      );
      return;
    }

    if (this.state.scene.data?.sceneID !== sceneID) {
      return;
    }

    this.applySceneFromApi(data, {
      selectedMotionValue: previousSelectedMotionValue,
      preferredReferenceName: previousSelectedReferenceName,
    });
  }

  private syncSelectedCharacter(availableCharacters: Character[]) {
    if (this.state.characters.resolvingMotionCharacter) {
      return;
    }

    Object.entries(this.state.characters.byReferenceName).forEach(
      ([referenceName, referenceState]) => {
        const reference = getMotionEditorActorByReferenceName(
          this.state.playback.sceneActors,
          referenceName,
        );
        const characterID =
          this.state.scene.data?.objects[referenceName]?.objectID ??
          reference?.characterID ??
          referenceState.characterSettings.charUploadID;
        const selectedCharacter = this.getCharacterDataById(
          characterID,
          availableCharacters,
        );
        if (!selectedCharacter) {
          return;
        }

        this.state.characters.byReferenceName[referenceName] = {
          ...referenceState,
          selectedCharacter: {
            ...selectedCharacter,
            rotationOffsets:
              selectedCharacter.characterID ===
              referenceState.selectedCharacter?.characterID
                ? referenceState.selectedCharacter?.rotationOffsets
                : selectedCharacter.rotationOffsets,
          },
          characterSettings: {
            ...referenceState.characterSettings,
            charUploadID: characterID,
            isLegacyCharacter: this.resolveIsLegacyCharacter(
              characterID,
              availableCharacters,
            ),
          },
        };
      },
    );

    const activeReferenceName = this.state.playback.selectedReferenceName;
    if (activeReferenceName) {
      this.applyActiveReferenceCharacterState(activeReferenceName);
      return;
    }

    const selectedCharacter = this.getCharacterDataById(
      this.state.characters.characterSettings.charUploadID,
      availableCharacters,
    );
    if (selectedCharacter) {
      this.state.characters.selectedCharacter = {
        ...selectedCharacter,
        rotationOffsets:
          selectedCharacter.characterID ===
          this.state.characters.selectedCharacter?.characterID
            ? this.state.characters.selectedCharacter?.rotationOffsets
            : selectedCharacter.rotationOffsets,
      };
    }
  }

  private getCharacterDataById(
    characterID: string,
    availableCharacters = this.state.characters.availableCharacters.data,
  ): CharacterData | null {
    const character = this.getKnownCharacterById(
      characterID,
      availableCharacters,
    );
    if (character) {
      const extension = (character as Character & { extension?: string })
        .extension;
      return {
        characterID: character.jobID,
        meshPath: character.meshPath,
        meshFormat: resolveCharacterMeshFormat(character.meshPath, extension),
        rotationOffsets: character.rotationOffsets,
        headBoneName: character.headBoneName,
      };
    }

    const builtinMeshPath = getBuiltinFreeCharacterMeshPath(characterID);
    if (builtinMeshPath) {
      return {
        characterID,
        meshPath: builtinMeshPath,
        meshFormat: resolveCharacterMeshFormat(builtinMeshPath),
        rotationOffsets:
          stripCharacterIdInstanceSuffix(characterID) ===
          DEFAULT_CHARACTER.jobID
            ? DEFAULT_CHARACTER.rotationOffsets
            : undefined,
      };
    }

    return null;
  }

  private getKnownCharacterById(
    characterID: string,
    availableCharacters = this.state.characters.availableCharacters.data,
  ): Character | null {
    const sceneCharacter = this.getSceneCharacterById(characterID);
    if (sceneCharacter) {
      return sceneCharacter;
    }

    const characterIDAliases = getCharacterIDAliases(characterID);
    const availableCharacter = availableCharacters?.find((nextCharacter) =>
      characterIDAliases.includes(nextCharacter.jobID),
    );
    if (availableCharacter) {
      return availableCharacter;
    }

    return null;
  }

  private getSceneCharacterById(characterID: string): Character | null {
    const characterIDAliases = getCharacterIDAliases(characterID);
    for (const characterIDAlias of characterIDAliases) {
      const sceneCharacter =
        this.state.characters.sceneCharactersByID[characterIDAlias];
      if (sceneCharacter) {
        return sceneCharacter;
      }
    }

    return null;
  }

  private syncLoadedSceneContext(
    scene: MotionEditorScene,
    selectedMotionValue?: string | null,
    preferredReferenceName?: string | null,
    preferredTimelineSelection?: PreferredTimelineSelection | null,
  ) {
    const playback = deriveMotionEditorScenePlayback(scene);
    this.syncSceneCharacterContexts(scene, playback.actors);
    const selectedMotion =
      getSelectedMotionFromTimelineSelection(
        playback.actors,
        preferredTimelineSelection,
      ) ?? getSelectedMotionFromValue(playback.actors, selectedMotionValue);
    const reference =
      getMotionEditorActorByReferenceName(
        playback.actors,
        selectedMotion?.referenceName,
      ) ??
      getMotionEditorActorByReferenceName(
        playback.actors,
        preferredReferenceName,
      ) ??
      getFirstMotionEditorActor(playback.actors);

    this.state.playback.sceneActors = playback.actors;
    this.pruneActorEditStates(playback.actors);

    if (!reference) {
      const sceneObjects = scene.objects ?? {};
      const objectEntries = Object.values(sceneObjects);
      const hasUnresolvedSceneObjects =
        playback.actors.length === 0 && objectEntries.length > 0;
      const isWaitingForSceneCharacters =
        hasUnresolvedSceneObjects &&
        this.hasPendingSceneCharacterMetadataLoad(scene);

      if (hasUnresolvedSceneObjects && !isWaitingForSceneCharacters) {
        console.warn(
          '[MotionEditor] Scene character references could not resolve to meshes.',
          {
            sceneID: scene.sceneID,
            objectIDs: objectEntries.map((object) => object.objectID),
            sceneCharacterIDs: Object.keys(
              this.state.characters.sceneCharactersByID,
            ),
          },
        );
        this.deps.global.noticesStore.error(
          'We could not load the character mesh for this scene. Try reloading, or switch the scene character from the Character panel.',
        );
      }
      this.state.playback.selectedReferenceName = null;
      this.state.playback.selectedSceneMotion = null;
      this.clearSelectedCharacterContext();
      this.state.characters.resolvingMotionCharacter =
        isWaitingForSceneCharacters;
      return;
    }

    this.applySelectedSceneReference(reference, selectedMotion);
  }

  private syncSceneCharacterContexts(
    scene: MotionEditorScene,
    references: MotionEditorActor[] = this.state.playback.sceneActors,
  ) {
    const nextByReferenceName: Record<string, SceneReferenceCharacterState> =
      {};

    for (const reference of references) {
      nextByReferenceName[reference.referenceName] =
        this.buildReferenceCharacterStateFromScene(
          scene,
          reference,
          this.state.characters.byReferenceName[reference.referenceName],
        );
    }

    this.state.characters.byReferenceName = nextByReferenceName;
  }

  private hydrateReferenceCharacterStateFromScene(
    referenceName: string,
  ): SceneReferenceCharacterState {
    const scene = this.state.scene.data;
    const reference = getMotionEditorActorByReferenceName(
      this.state.playback.sceneActors,
      referenceName,
    );

    if (!scene || !reference) {
      return {
        selectedCharacter: null,
        characterSettings: DEFAULT_CHARACTER_SETTINGS,
      };
    }

    const nextState = this.buildReferenceCharacterStateFromScene(
      scene,
      reference,
      this.state.characters.byReferenceName[referenceName],
    );
    this.state.characters.byReferenceName[referenceName] = nextState;
    return nextState;
  }

  private buildReferenceCharacterStateFromScene(
    scene: MotionEditorScene,
    reference: MotionEditorActor,
    existingState?: SceneReferenceCharacterState,
  ): SceneReferenceCharacterState {
    const sceneObject = scene.objects[reference.referenceName];
    const characterID = sceneObject?.objectID ?? reference.characterID;
    const selectedCharacter = this.buildCharacterDataForSceneReference(
      characterID,
      reference,
      sceneObject,
    );
    const baseSettings =
      existingState?.characterSettings ??
      this.getInitialCharacterSettings(scene, characterID);

    return {
      selectedCharacter,
      characterSettings: {
        ...baseSettings,
        charUploadID: characterID,
        isLegacyCharacter: this.resolveIsLegacyCharacter(characterID),
      },
    };
  }

  private buildCharacterDataForSceneReference(
    characterID: string,
    reference: MotionEditorActor,
    sceneObject?: MotionEditorScene['objects'][string],
  ): CharacterData {
    return (
      this.getCharacterDataById(characterID) ?? {
        characterID,
        meshPath: reference.meshPath ?? sceneObject?.meshURL ?? '',
        identityJsonURL:
          reference.identityJsonURL ?? sceneObject?.identityJsonURL,
        rotationOffsets:
          reference.rotationOffsets ?? sceneObject?.rotationOffsets,
        headBoneName: reference.headBoneName,
      }
    );
  }

  private getActiveReferenceNameForCharacterSwap() {
    const { selectedReferenceName, sceneActors } = this.state.playback;
    if (selectedReferenceName) {
      return selectedReferenceName;
    }

    return sceneActors[0]?.referenceName ?? null;
  }

  private applySceneFromApi(
    apiScene: Scene,
    options?: {
      selectedMotionValue?: string | null;
      preferredReferenceName?: string | null;
      preferredTimelineSelection?: PreferredTimelineSelection | null;
    },
  ) {
    const sceneCharacterLoad = this.ensureSceneCharactersForApiScene(apiScene);
    const sceneApplyID = this.startSceneApply();
    const nextScene = this.createMotionEditorScene(apiScene);
    this.state.scene = {
      status: 'ready',
      data: nextScene,
    };
    this.syncLoadedSceneContext(
      nextScene,
      options?.selectedMotionValue,
      options?.preferredReferenceName,
      options?.preferredTimelineSelection,
    );
    void this.enrichSceneWithSceneCharacters(
      apiScene,
      sceneApplyID,
      options,
      sceneCharacterLoad,
    );
  }

  private async enrichSceneWithSceneCharacters(
    apiScene: Scene,
    sceneApplyID: number,
    options?: {
      selectedMotionValue?: string | null;
      preferredReferenceName?: string | null;
      preferredTimelineSelection?: PreferredTimelineSelection | null;
    },
    sceneCharacterLoad = this.ensureSceneCharactersForApiScene(apiScene),
  ) {
    const sceneID = apiScene.sceneID;

    try {
      await sceneCharacterLoad;
      if (!this.isCurrentSceneApply(sceneID, sceneApplyID)) {
        return;
      }

      await this.resolveHeadBoneNamesForSceneObjects(apiScene);
      if (!this.isCurrentSceneApply(sceneID, sceneApplyID)) {
        return;
      }

      const selectedMotionValue =
        options?.selectedMotionValue ??
        this.state.playback.selectedSceneMotion?.motionValue;
      const preferredReferenceName =
        options?.preferredReferenceName ??
        this.state.playback.selectedReferenceName;
      const preferredTimelineSelection =
        options?.preferredTimelineSelection ?? null;

      const nextScene = this.createMotionEditorScene(apiScene);
      this.state.scene = {
        status: 'ready',
        data: nextScene,
      };
      this.syncLoadedSceneContext(
        nextScene,
        selectedMotionValue,
        preferredReferenceName,
        preferredTimelineSelection,
      );
    } catch {
      // Scene is already usable without targeted character enrichment.
    }
  }

  private startSceneApply() {
    this.sceneApplyRequestID += 1;
    return this.sceneApplyRequestID;
  }

  private isCurrentSceneApply(sceneID: string, sceneApplyID: number) {
    return (
      this.sceneApplyRequestID === sceneApplyID &&
      this.state.scene.data?.sceneID === sceneID
    );
  }

  private ensureSceneCharactersForApiScene(apiScene: Scene) {
    const characterIDs = this.getSceneCharacterIDsToFetch(apiScene);
    if (characterIDs.length === 0) {
      return Promise.resolve();
    }

    characterIDs.forEach((characterID) => {
      this.sceneCharacterLoadIDs.add(characterID);
    });

    return this.getApiKeyData()
      .then(async (apiKeyData) => {
        if (!apiKeyData) {
          return;
        }

        await Promise.all(
          characterIDs.map((characterID) =>
            this.loadSceneCharacter(characterID, apiKeyData.apiKey),
          ),
        );
      })
      .finally(() => {
        characterIDs.forEach((characterID) => {
          if (!this.sceneCharacterRequestsByID.has(characterID)) {
            this.sceneCharacterLoadIDs.delete(characterID);
          }
        });
      });
  }

  private getSceneCharacterIDsToFetch(apiScene: Scene) {
    if (apiScene.type !== SceneType.MOTION || !apiScene.objects) {
      return [];
    }

    const characterIDs = new Set<string>();
    Object.values(apiScene.objects).forEach((sceneObject) => {
      const characterID = sceneObject.objectID?.trim();
      if (!characterID) {
        return;
      }

      const requestCharacterID = stripCharacterIdInstanceSuffix(characterID);
      if (this.getSceneCharacterById(requestCharacterID)) {
        return;
      }

      characterIDs.add(requestCharacterID);
    });

    return Array.from(characterIDs);
  }

  private loadSceneCharacter(characterID: string, apiKey: string) {
    if (this.getSceneCharacterById(characterID)) {
      return Promise.resolve();
    }

    const existingRequest = this.sceneCharacterRequestsByID.get(characterID);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async () => {
      const { data } = await getExternalCharacter(apiKey, characterID);
      if (data) {
        this.cacheSceneCharacter(characterID, data);
      }
    })().finally(() => {
      this.sceneCharacterRequestsByID.delete(characterID);
      this.sceneCharacterLoadIDs.delete(characterID);
    });

    this.sceneCharacterRequestsByID.set(characterID, request);
    return request;
  }

  private cacheSceneCharacter(requestedCharacterID: string, character: Character) {
    for (const characterID of getCharacterIDAliases(requestedCharacterID)) {
      this.state.characters.sceneCharactersByID[characterID] = character;
    }
    for (const characterID of getCharacterIDAliases(character.jobID)) {
      this.state.characters.sceneCharactersByID[characterID] = character;
    }
  }

  private hasPendingSceneCharacterMetadataLoad(scene: MotionEditorScene) {
    return Object.values(scene.objects).some((sceneObject) => {
      if (sceneObject.meshURL) {
        return false;
      }

      const characterID = sceneObject.objectID;
      return getCharacterIDAliases(characterID).some(
        (characterIDAlias) =>
          this.sceneCharacterLoadIDs.has(characterIDAlias) ||
          this.sceneCharacterRequestsByID.has(characterIDAlias),
      );
    });
  }

  private async updateSceneReferenceCharacter(
    scene: MotionEditorScene,
    referenceName: string,
    characterID: string,
  ) {
    const previousSelectedMotionValue =
      this.state.playback.selectedSceneMotion?.motionValue;
    const sceneObject = scene.objects[referenceName];
    if (!sceneObject) {
      this.deps.global.noticesStore.error('Scene reference not found.');
      return;
    }

    if (sceneObject.objectID === characterID) {
      const characterData = this.getCharacterDataById(characterID);
      if (characterData) {
        this.state.characters.byReferenceName[referenceName] = {
          selectedCharacter: characterData,
          characterSettings: {
            ...this.getReferenceCharacterState(referenceName).characterSettings,
            charUploadID: characterID,
            isLegacyCharacter: this.resolveIsLegacyCharacter(characterID),
          },
        };
        if (this.state.playback.selectedReferenceName === referenceName) {
          this.applyActiveReferenceCharacterState(referenceName);
        }
      }
      this.state.characters.resolvingMotionCharacter = false;
      return;
    }

    const apiKeyData = await this.getApiKeyData();
    if (!apiKeyData) {
      this.deps.global.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      return;
    }

    this.state.characters.resolvingMotionCharacter = true;
    delete this.state.editing.actorEditStatesById[referenceName];

    const { data, error } = await postExternalSceneReference(
      apiKeyData.apiKey,
      scene.sceneID,
      referenceName,
      {
        objectID: characterID,
        stitchFromIndex: 0,
        timeline: sceneObject.timeline.map(sceneTimelineItemToUpdateItem),
      },
    );

    if (error || !data) {
      this.state.characters.resolvingMotionCharacter = false;
      this.deps.global.noticesStore.error(
        error?.message ?? 'Failed to update scene character.',
      );
      return;
    }

    this.applySceneFromApi(data, {
      selectedMotionValue: previousSelectedMotionValue,
      preferredReferenceName: referenceName,
    });
  }

  private applySelectedSceneReference(
    reference: MotionEditorActor,
    selectedMotion: SelectedSceneMotion | null = null,
  ) {
    const previousSelectedMotionValue =
      this.state.playback.selectedSceneMotion?.motionValue ?? null;
    const nextSelectedMotion =
      selectedMotion ??
      this.getStoredSelectedMotion(reference) ??
      getDefaultSelectedMotion(reference);
    const selectedMotionValue = nextSelectedMotion?.motionValue ?? null;

    if (
      previousSelectedMotionValue &&
      previousSelectedMotionValue !== selectedMotionValue
    ) {
      this.state.motionDescription = '';
    }

    this.state.playback.selectedReferenceName = reference.referenceName;
    this.state.playback.selectedSceneMotion = nextSelectedMotion;
    this.applyActiveReferenceCharacterState(reference.referenceName);
    this.state.characters.resolvingMotionCharacter = false;

    this.ensureActorEditState(reference.referenceName, selectedMotionValue);
  }

  private applyActiveReferenceCharacterState(referenceName: string) {
    const referenceState = this.state.scene.data
      ? this.hydrateReferenceCharacterStateFromScene(referenceName)
      : this.getReferenceCharacterState(referenceName);
    this.state.characters.selectedCharacter = referenceState.selectedCharacter;
    this.state.characters.characterSettings = referenceState.characterSettings;
  }

  private getStoredSelectedMotion(reference: MotionEditorActor) {
    const selectedMotionValue =
      this.state.editing.actorEditStatesById[reference.referenceName]
        ?.selectedMotionValue;

    if (!selectedMotionValue) {
      return null;
    }

    return (
      reference.timelineMotions.find(
        (motion) => motion.motionValue === selectedMotionValue,
      ) ?? null
    );
  }

  private ensureActorEditState(
    referenceName: string,
    selectedMotionValue: string | null,
  ) {
    const existingEditState =
      this.state.editing.actorEditStatesById[referenceName];
    if (existingEditState?.selectedMotionValue === selectedMotionValue) {
      return existingEditState;
    }

    const nextEditState = createInitialActorEditingState(selectedMotionValue);
    this.state.editing.actorEditStatesById[referenceName] = nextEditState;
    return nextEditState;
  }

  private pruneActorEditStates(actors: MotionEditorActor[]) {
    const referenceNames = new Set(actors.map((actor) => actor.referenceName));
    Object.keys(this.state.editing.actorEditStatesById).forEach(
      (referenceName) => {
        if (!referenceNames.has(referenceName)) {
          delete this.state.editing.actorEditStatesById[referenceName];
        }
      },
    );
  }

  private clearSelectedCharacterContext() {
    this.state.characters.selectedCharacter = null;
    this.state.characters.resolvingMotionCharacter = true;
    this.state.characters.characterSettings = {
      ...DEFAULT_CHARACTER_SETTINGS,
      charUploadID: '',
    };
    this.state.characters.byReferenceName = {};
  }

  private setDefaultSelectedCharacterContext() {
    this.state.characters.selectedCharacter = {
      characterID: DEFAULT_CHARACTER.jobID,
      meshPath: DEFAULT_CHARACTER.meshPath,
      rotationOffsets: DEFAULT_CHARACTER.rotationOffsets,
    };
    this.state.characters.characterSettings = DEFAULT_CHARACTER_SETTINGS;
    this.state.characters.resolvingMotionCharacter = false;
  }

  private getInitialCharacterSettings(
    scene: { sceneSettings?: Scene['sceneSettings'] } | null,
    selectedCharacterID: string,
  ): MotionEditorCharacterSettings {
    const sceneCharacterSettings = scene?.sceneSettings?.characterSettings;

    return {
      charUploadID: selectedCharacterID,
      isLegacyCharacter: this.resolveIsLegacyCharacter(selectedCharacterID),
      skinHex: sceneCharacterSettings?.skinHex ?? 'default',
      faceExpression: normalizeFaceExpression(
        sceneCharacterSettings?.faceExpression,
      ),
      staticHands: sceneCharacterSettings?.staticHands ?? HAND_OPTIONS.DEFAULT,
    };
  }

  private resolveIsLegacyCharacter(
    characterID: string,
    availableCharacters = this.state.characters.availableCharacters.data,
  ) {
    if (isLegacyMotionEditorCharacterId(characterID)) {
      return true;
    }

    const character = this.getKnownCharacterById(
      characterID,
      availableCharacters,
    );
    if (character) {
      return resolveIsLegacyMotionEditorCharacter({
        characterID,
        createdBy: character.createdBy,
      });
    }

    if (getBuiltinFreeCharacterMeshPath(characterID)) {
      return false;
    }

    return false;
  }

  private async getApiKeyData() {
    if (this.deps.global.authStore.state.authStatus === 'anonymous') {
      return null;
    }

    const apiKeyData = await waitForAsyncState(
      this.deps.global.userStore.state,
      (state) => state.apiKey,
    );

    return apiKeyData?.apiKey ? apiKeyData : null;
  }

  private async resolveHeadBoneNamesForSceneObjects(scene: Scene) {
    if (scene.type !== SceneType.MOTION || !scene.objects) {
      return;
    }

    const objectIds = [
      ...new Set(
        Object.values(scene.objects)
          .map((object) => object.objectID)
          .filter(
            (id): id is string =>
              typeof id === 'string' && id.trim().length > 0,
          ),
      ),
    ];

    if (objectIds.length === 0) {
      return;
    }

    await Promise.all(
      objectIds.map(async (objectId) => {
        const character = this.getKnownCharacterById(objectId);
        if (!character?.characterConfigURL || character.headBoneConfigFetched) {
          return;
        }

        character.headBoneConfigFetched = true;
        const headBoneName = await fetchHeadBoneNameFromConfigUrl(
          character.characterConfigURL,
        );
        if (headBoneName) {
          character.headBoneName = headBoneName;
        }
      }),
    );
  }

  private createMotionEditorScene(scene: Scene) {
    return createMotionEditorSceneFromScene(scene, (characterID) =>
      this.getCharacterDataById(characterID),
    );
  }
}

function createInitialEditingState(): EditingState {
  return {
    actorEditStatesById: {},
  };
}

function getSceneLoadKey(
  sceneID: string,
  selectedMotionValue?: string | null,
) {
  return JSON.stringify([sceneID, selectedMotionValue ?? null]);
}

function getSelectedMotionFromTimelineSelection(
  actors: MotionEditorActor[],
  selection?: PreferredTimelineSelection | null,
): SelectedSceneMotion | null {
  if (!selection) {
    return null;
  }

  const actor = getMotionEditorActorByReferenceName(
    actors,
    selection.referenceName,
  );

  return actor?.timelineMotions[selection.timelineIndex] ?? null;
}

function createInitialActorEditingState(
  selectedMotionValue: string | null,
): ActorEditingState {
  return {
    selectedMotionValue,
  };
}

export const { useStore, useStoreApi, withProvider, Context } =
  createStoreProvider({
    Store: MotionEditorStore,
    displayName: 'MotionEditorStore',
    staticDeps: { global: globalStores },
  });

function normalizeFaceExpression(faceExpression?: string) {
  if (!faceExpression) {
    return FACE_EXPRESSIONS.DEFAULT;
  }

  const normalizedFace = faceExpression.toLowerCase();
  switch (normalizedFace) {
    case 'neutral':
      return FACE_EXPRESSIONS.NEUTRAL;
    case 'happy':
      return FACE_EXPRESSIONS.DELIGHTED;
    case 'sad':
      return FACE_EXPRESSIONS.SAD;
    case 'angry':
      return FACE_EXPRESSIONS.ANGRY;
    case 'surprised':
      return FACE_EXPRESSIONS.SURPRISE;
    case 'hurt':
      return FACE_EXPRESSIONS.PAIN;
    case 'thinking':
      return FACE_EXPRESSIONS.THINKING_LOOK_RIGHT;
    case 'confused':
      return FACE_EXPRESSIONS.CONFUSION;
    case 'relieved':
      return FACE_EXPRESSIONS.PLEASED;
    case 'bored':
      return FACE_EXPRESSIONS.BORED;
    default:
      return Object.values(FACE_EXPRESSIONS).includes(
        faceExpression as FACE_EXPRESSIONS,
      )
        ? faceExpression
        : FACE_EXPRESSIONS.NEUTRAL;
  }
}

function sceneTimelineItemToUpdateItem(
  item: Scene['objects'][string]['timeline'][number],
): SceneTimelineUpdateItem {
  return {
    appliedMotionEditJobID: item.appliedMotionEditJobID,
    jobID: item.jobID,
    title: item.title,
    trimEnd: item.trimEnd,
    trimStart: item.trimStart,
    variationNumber: item.variationNumber,
  };
}

/**
 * Scene objectIDs must match getCharacters jobIDs. Expand to include the base character ID
 * and the character ID with the instance suffix removed so
 * meshPath resolves and deriveMotionEditorActors does not drop the actor.
 */
function getCharacterIDAliases(characterID: string): string[] {
  const aliases = new Set<string>([characterID]);
  const base = stripCharacterIdInstanceSuffix(characterID);
  aliases.add(base);
  return Array.from(aliases);
}
